import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function QuoteDialog({ product, open, onOpenChange }) {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(product?.moq || 1);
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      onOpenChange(false);
      navigate(`/login?next=/product/${product.id}`);
      return;
    }
    setLoading(true);
    try {
      await api("/rfq", {
        method: "POST",
        auth: true,
        body: {
          product_name: product.title,
          product_id: product.id,
          quantity: Number(quantity),
          unit: product.unit || "piece",
          details,
        },
      });
      toast.success(t("req_quote_t"));
      onOpenChange(false);
      setDetails("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("req_quote_t")}</DialogTitle>
          <DialogDescription>
            {product?.supplier_name} — “{product?.title}”
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="quote-qty">
              {t("qty")} (MOQ {product?.moq} {product?.unit || "pcs"})
            </label>
            <input
              id="quote-qty"
              data-testid="quote-quantity-input"
              type="number"
              min={product?.moq || 1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="quote-details">
              {t("details_supplier")}
            </label>
            <textarea
              id="quote-details"
              data-testid="quote-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <button
            data-testid="quote-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-teal-700 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
          >
            {loading ? t("sending") : user ? t("sending_order") : t("login_quote")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
